import React, { useEffect, useState, useRef } from 'react';
import { Plus, Search, Edit3, User, Calendar, Upload, Save, X, ArrowRightLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import api from '../services/api';
import * as XLSX from 'xlsx';
import ImportModal from '../components/ImportModal';
import IconButton from '../components/IconButton';

const Cliente = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [isSaving, setIsSaving] = useState(false);

  const importFields = [
    { key: 'nome_cli', label: 'Nome Cliente', required: true },
    { key: 'cnpj_cli', label: 'CNPJ', required: false },
    { key: 'cd_supera_cli', label: 'Cód. Supera', required: false },
    { key: 'cod_sub_cli', label: 'Cód. Sub', required: false },
    { key: 'ativo_cli', label: 'Ativo (1=Sim, 0=Não)', required: false },
    { key: 'dt_cadastro_cli', label: 'Data Cadastro', required: false },
    { key: 'dt_desativa_cli', label: 'Data Desativação', required: false }
  ];

  const handleImportData = async (data) => {
    setShowImportModal(false);
    const toastId = toast.loading('Importando clientes...');
    let successCount = 0;
    let errorCount = 0;

    const parseExcelDate = (val) => {
       if (!val) return null;
       if (typeof val === 'number') {
           const date = XLSX.SSF.parse_date_code(val);
           return `${date.y}-${String(date.m).padStart(2,'0')}-${String(date.d).padStart(2,'0')}`;
       }
       if (typeof val === 'string') {
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
                const [d, m, y] = val.split('/');
                return `${y}-${m}-${d}`;
            }
            if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
       }
       return null;
    };

    // Sequential import since backend doesn't support bulk yet
    for (const row of data) {
        try {
            const payload = {
                nome_cli: row.nome_cli,
                cnpj_cli: row.cnpj_cli ? String(row.cnpj_cli).replace(/[^0-9]/g, '') : '',
                cd_supera_cli: row.cd_supera_cli,
                cod_sub_cli: row.cod_sub_cli,
                ativo_cli: row.ativo_cli !== undefined && row.ativo_cli !== '' ? row.ativo_cli : 1,
                dt_cadastro_cli: parseExcelDate(row.dt_cadastro_cli),
                dt_desativa_cli: parseExcelDate(row.dt_desativa_cli)
            };
            
            // Clean empty values
            Object.keys(payload).forEach(key => {
                if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
                    delete payload[key];
                }
            });

            await api.post('/cliente', payload);
            successCount++;
        } catch (err) {
            console.error('Falha ao importar linha', row, err);
            errorCount++;
        }
    }
    
    toast.dismiss(toastId);
    if (errorCount === 0) {
        toast.success(`${successCount} clientes importados com sucesso!`);
    } else {
        toast.success(`${successCount} importados. ${errorCount} falhas.`);
    }
    load();
  };

  const [formData, setFormData] = useState({
    cod_sub_cli: '',
    cd_supera_cli: '',
    nome_cli: '',
    cnpj_cli: '',
    ativo_cli: '',
    dt_cadastro_cli: '',
    dt_desativa_cli: '',
    id_usu: '',
    id_cdl_ace: ''
  });

  const cadastroDateRef = useRef(null);
  const desativaDateRef = useRef(null);
  const transferSearchRef = useRef(null);
  const [transferEnabled, setTransferEnabled] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferSearch, setTransferSearch] = useState('');
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferOptions, setTransferOptions] = useState([]);
  const [transferLoading, setTransferLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dados');

  useEffect(() => { document.title = 'Cliente - EstOX'; }, []);
  useEffect(() => { load(); }, []);
  useEffect(() => {
    let abort = false;
    let t = null;
    if (!transferOpen) { setTransferOptions([]); return; }
    const q = String(transferSearch || '').trim();
    if (!q) { setTransferOptions(items); return; }
    setTransferLoading(true);
    t = setTimeout(async () => {
      try {
        const resp = await api.get('/cliente', { params: { search: q } });
        if (!abort) setTransferOptions(resp.data?.data || []);
      } catch (e) {
        if (!abort) setTransferOptions([]);
      } finally {
        if (!abort) setTransferLoading(false);
      }
    }, 300);
    return () => { abort = true; if (t) clearTimeout(t); };
  }, [transferSearch, transferOpen, items]);
  useEffect(() => {
    if (!transferOpen) return;
    const t = setTimeout(() => {
      transferSearchRef.current?.focus?.();
    }, 0);
    return () => clearTimeout(t);
  }, [transferOpen]);

  const load = async () => {
    try {
      const response = await api.get('/cliente');
      setItems(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter(i => (
    String(i.nome_cli ?? '').toLowerCase().includes(String(searchTerm).toLowerCase()) ||
    String(i.cd_supera_cli ?? '').toLowerCase().includes(String(searchTerm).toLowerCase()) ||
    String(i.cnpj_cli ?? '').toLowerCase().includes(String(searchTerm).toLowerCase())
  ));

  const openModal = (item = null) => {
    setEditingItem(item);
    setFormData({
      cod_sub_cli: item?.cod_sub_cli ?? '',
      cd_supera_cli: item?.cd_supera_cli || '',
      nome_cli: item?.nome_cli || '',
      cnpj_cli: formatCNPJInput(item?.cnpj_cli || ''),
      ativo_cli: (item?.ativo_cli === null || item?.ativo_cli === undefined) ? '' : String(item.ativo_cli),
      dt_cadastro_cli: isoToDisplay(item?.dt_cadastro_cli || ''),
      dt_desativa_cli: isoToDisplay(item?.dt_desativa_cli || ''),
      id_usu: item?.id_usu ?? '',
      id_cdl_ace: item?.id_cdl_ace || ''
    });
    setShowModal(true);
    setTransferEnabled(false);
    setTransferTargetId('');
    setTransferSearch('');
    setActiveTab('dados');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({ cod_sub_cli: '', cd_supera_cli: '', nome_cli: '', ativo_cli: '', dt_cadastro_cli: '', dt_desativa_cli: '', id_usu: '', id_cdl_ace: '' });
    setTransferEnabled(false);
    setTransferTargetId('');
    setTransferSearch('');
    setActiveTab('dados');
  };

  const renderStatus = (v) => {
    const n = (v === '' || v === null || v === undefined) ? null : Number(v);
    if (n === 1) return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">ATIVO</span>;
    if (n === 0) return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">INATIVO</span>;
    return '-';
  };

  const fmtDateBR = (s) => {
    if (!s) return '-';
    try {
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return s;
      const dd = String(d.getDate()).padStart(2,'0');
      const mm = String(d.getMonth()+1).padStart(2,'0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch { return s; }
  };

  const formatDateInput = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0,8);
    const dd = digits.slice(0,2);
    const mm = digits.slice(2,4);
    const yyyy = digits.slice(4,8);
    let out = '';
    if (dd) out += dd;
    if (mm) out += '/' + mm;
    if (yyyy) out += '/' + yyyy;
    return out;
  };

  const toISODate = (br) => {
    const m = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/.exec(String(br || ''));
    if (!m) return null;
    const dd = m[1], mm = m[2], yyyy = m[3];
    return `${yyyy}-${mm}-${dd}`;
  };

  const isoToDisplay = (iso) => {
    const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(String(iso || ''));
    if (!m) return '';
    const yyyy = m[1], mm = m[2], dd = m[3];
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatCNPJ = (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(0,14);
    if (!digits) return '-';
    const p1 = digits.slice(0,2);
    const p2 = digits.slice(2,5);
    const p3 = digits.slice(5,8);
    const p4 = digits.slice(8,12);
    const p5 = digits.slice(12,14);
    let out = '';
    if (p1) out += p1;
    if (p2) out += '.' + p2;
    if (p3) out += '.' + p3;
    if (p4) out += '/' + p4;
    if (p5) out += '-' + p5;
    return out;
  };

  const formatCNPJInput = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0,14);
    const p1 = digits.slice(0,2);
    const p2 = digits.slice(2,5);
    const p3 = digits.slice(5,8);
    const p4 = digits.slice(8,12);
    const p5 = digits.slice(12,14);
    let out = '';
    if (p1) out += p1;
    if (p2) out += '.' + p2;
    if (p3) out += '.' + p3;
    if (p4) out += '/' + p4;
    if (p5) out += '-' + p5;
    return out;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.nome_cli.trim()) { toast.error('Nome é obrigatório'); return; }
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        cnpj_cli: String(formData.cnpj_cli || '').replace(/\D/g, ''),
        dt_cadastro_cli: toISODate(formData.dt_cadastro_cli),
        dt_desativa_cli: toISODate(formData.dt_desativa_cli)
      };
      if (editingItem) {
        await api.put(`/cliente/${editingItem.id_cli}`, payload);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await api.post('/cliente', payload);
        toast.success('Cliente criado com sucesso!');
      }
      closeModal();
      load();
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      const msg = error?.response?.data?.message || 'Erro ao salvar cliente';
      toast.error(msg);
    } finally { setIsSaving(false); }
  };

  const handleTransfer = async () => {
    if (!editingItem) { toast.error('Abra o cliente para transferir'); return; }
    if (!transferEnabled) { toast.error('Ative a transferência'); return; }
    if (!transferTargetId) { toast.error('Selecione o cliente destino'); return; }
    setIsSaving(true);
    try {
      await api.post(`/cliente/${editingItem.id_cli}/transfer`, { target_id: transferTargetId });
      toast.success('Transferência concluída');
      closeModal();
      load();
    } catch (error) {
      console.error('Erro na transferência:', error);
      const msg = error?.response?.data?.message || 'Erro na transferência';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };
  

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Cliente</h1>
        <p className="text-gray-600">Gerencie clientes</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <User className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total de Clientes</p>
              <p className="text-2xl font-bold text-gray-900">{items.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Search className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Filtrados</p>
              <p className="text-2xl font-bold text-gray-900">{filtered.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Plus className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ações</p>
              <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Novo Cliente" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Buscar clientes..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IconButton icon={Upload} onClick={() => setShowImportModal(true)} variant="success" title="Importar" />
              <IconButton icon={Plus} onClick={() => openModal()} variant="primary" title="Novo Cliente" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código Supera</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CNPJ</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cod Sub</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CDL/ACE</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ativo</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cadastro</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Desativação</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuário</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map((item) => (
                <tr key={item.id_cli} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs text-gray-900">{item.id_cli}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">{item.nome_cli || '-'}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">{item.cd_supera_cli || '-'}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">{formatCNPJ(item.cnpj_cli)}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">{item.cod_sub_cli ?? '-'}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">{item.id_cdl_ace || '-'}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">{renderStatus(item.ativo_cli)}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">{fmtDateBR(item.dt_cadastro_cli)}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">{fmtDateBR(item.dt_desativa_cli)}</td>
                  <td className="px-4 py-2 text-xs text-gray-900">{item.usuario_nome || '-'}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-xs font-medium">
                    <div className="flex space-x-2">
                      <IconButton icon={Edit3} onClick={() => openModal(item)} variant="warning" size="sm" title="Editar" />
                      
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum cliente encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">{searchTerm ? 'Tente ajustar sua busca.' : 'Comece criando um novo cliente.'}</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-6">
          <div className="relative mx-auto p-6 border w-full max-w-5xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{editingItem ? 'Editar Cliente' : 'Novo Cliente'}</h3>
              <div className="flex border-b mb-4">
                <button
                  type="button"
                  className={`px-4 py-2 ${activeTab === 'dados' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
                  onClick={() => setActiveTab('dados')}
                >
                  Dados
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 ${activeTab === 'transfer' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'} ${!editingItem ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={() => editingItem && setActiveTab('transfer')}
                >
                  Transferência
                </button>
              </div>
              <form onSubmit={handleSave} className="space-y-4">
                {activeTab === 'dados' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                      <input type="text" required maxLength={90} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={formData.nome_cli} onChange={(e) => setFormData({ ...formData, nome_cli: e.target.value.toUpperCase() })} />
                      <div className="mt-1 text-xs text-gray-500">{formData.nome_cli.length}/90</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        value={formData.cnpj_cli}
                        onChange={(e) => setFormData({ ...formData, cnpj_cli: formatCNPJInput(e.target.value) })}
                        placeholder="00.000.000/0000-00"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Código Supera</label>
                        <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={formData.cd_supera_cli} onChange={(e) => setFormData({ ...formData, cd_supera_cli: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cod Sub</label>
                        <input type="number" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={formData.cod_sub_cli} onChange={(e) => setFormData({ ...formData, cod_sub_cli: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">CDL/ACE</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          value={formData.id_cdl_ace}
                          onChange={(e) => setFormData({ ...formData, id_cdl_ace: e.target.value })}
                        >
                          <option value="">Selecione</option>
                          <option value="CDL">CDL</option>
                          <option value="ACE">ACE</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Ativo</label>
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          value={formData.ativo_cli}
                          onChange={(e) => setFormData({ ...formData, ativo_cli: e.target.value, dt_desativa_cli: e.target.value === '0' ? formData.dt_desativa_cli : '' })}
                        >
                          <option value="">Selecione</option>
                          <option value="1">ATIVO</option>
                          <option value="0">INATIVO</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Usuário</label>
                        <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">{user?.name || '-'}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Data Cadastro</label>
                        <div className="relative">
                          <input
                            type="text"
                            className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="dd/mm/aaaa"
                            value={formData.dt_cadastro_cli}
                            onChange={(e) => setFormData({ ...formData, dt_cadastro_cli: formatDateInput(e.target.value) })}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                            onClick={() => {
                              if (cadastroDateRef.current?.showPicker) cadastroDateRef.current.showPicker();
                              else cadastroDateRef.current?.focus();
                            }}
                            title="Selecionar data"
                          >
                            <Calendar className="h-5 w-5" />
                          </button>
                          <input
                            type="date"
                            ref={cadastroDateRef}
                            className="hidden"
                            onChange={(e) => setFormData({ ...formData, dt_cadastro_cli: isoToDisplay(e.target.value) })}
                          />
                        </div>
                      </div>
                      {String(formData.ativo_cli) === '0' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Data Desativação</label>
                          <div className="relative">
                            <input
                              type="text"
                              className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              placeholder="dd/mm/aaaa"
                              value={formData.dt_desativa_cli}
                              onChange={(e) => setFormData({ ...formData, dt_desativa_cli: formatDateInput(e.target.value) })}
                            />
                            <button
                              type="button"
                              className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                              onClick={() => {
                                if (desativaDateRef.current?.showPicker) desativaDateRef.current.showPicker();
                                else desativaDateRef.current?.focus();
                              }}
                              title="Selecionar data"
                            >
                              <Calendar className="h-5 w-5" />
                            </button>
                            <input
                              type="date"
                              ref={desativaDateRef}
                              className="hidden"
                              onChange={(e) => setFormData({ ...formData, dt_desativa_cli: isoToDisplay(e.target.value) })}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
                {activeTab === 'transfer' && editingItem && (
                  <div className="pt-2">
                    <div className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={transferEnabled}
                        onChange={(e) => setTransferEnabled(e.target.checked)}
                        id="transferToggle"
                      />
                      <label htmlFor="transferToggle" className="text-sm font-medium text-gray-700">Ativar transferência de movimentações</label>
                    </div>
                    {transferEnabled && (
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Cliente destino (ativo)</label>
                          <div className="relative">
                            <button
                              type="button"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-left focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              onClick={() => setTransferOpen(!transferOpen)}
                            >
                              {(() => {
                                const sel = transferOptions.find(i => i.id_cli === Number(transferTargetId)) || items.find(i => i.id_cli === Number(transferTargetId));
                                return sel ? `${sel.id_cli} - ${sel.nome_cli}` : 'Selecione';
                              })()}
                            </button>
                            {transferOpen && (
                              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                                <div className="p-2 border-b border-gray-200">
                                  <input
                                    type="text"
                                    ref={transferSearchRef}
                                    className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Pesquisar por nome, código, ID ou CNPJ"
                                    value={transferSearch}
                                    onChange={(e) => setTransferSearch(e.target.value)}
                                  />
                                </div>
                                <div className="max-h-56 overflow-auto">
                                  {transferLoading && <div className="px-3 py-2 text-gray-500 text-sm">Buscando...</div>}
                                  {!transferLoading && transferOptions
                                    .filter(i => Number(i.ativo_cli) === 1 && i.id_cli !== editingItem.id_cli)
                                    .sort((a,b) => String(a.nome_cli || '').localeCompare(String(b.nome_cli || ''), 'pt-BR', { sensitivity: 'base' }))
                                    .map(i => (
                                      <div
                                        key={i.id_cli}
                                        className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                                        onClick={() => { setTransferTargetId(i.id_cli); setTransferOpen(false); }}
                                      >
                                        {`${i.id_cli} - ${i.nome_cli}`}
                                      </div>
                                    ))}
                                  {!transferLoading && transferOptions
                                    .filter(i => Number(i.ativo_cli) === 1 && i.id_cli !== editingItem.id_cli).length === 0 && (
                                      <div className="px-3 py-2 text-gray-500 text-sm">Nenhum resultado</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end space-x-3 pt-4">
                  <IconButton icon={X} onClick={closeModal} variant="secondary" title="Cancelar" />
                  {activeTab === 'dados' ? (
                    <IconButton icon={Save} type="submit" disabled={isSaving} variant="primary" title={isSaving ? 'Salvando...' : (editingItem ? 'Atualizar' : 'Criar')} />
                  ) : (
                    <IconButton icon={ArrowRightLeft} onClick={handleTransfer} disabled={isSaving || !transferTargetId || !transferEnabled} variant="info" title={isSaving ? 'Transferindo...' : 'Transferir'} />
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportData}
        fields={importFields}
      />
    </div>
  );
};

export default Cliente;
